import React, { Component } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import './add-domain.css';

const DOMAIN_TYPE_PRIMARY = 'primary';
const DOMAIN_TYPE_SUB = 'sub';
const PRIMARY_DOMAIN_SEARCH_DEBOUNCE_MS = 400;
/** Fixed dropdown height; extra options scroll inside the menu */
const PRIMARY_DOMAIN_MENU_MAX_HEIGHT = 220;

const PRIMARY_DOMAIN_SELECT_STYLES = {
    control: (base, state) => ({
        ...base,
        fontSize: '13px',
        minHeight: '38px',
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '2px 8px',
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: '13px',
        color: '#6c757d',
    }),
    singleValue: (base) => ({
        ...base,
        fontSize: '13px',
        color: '#212529',
    }),
    input: (base) => ({
        ...base,
        fontSize: '13px',
        margin: 0,
        padding: 0,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        minHeight: '36px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: state.isSelected
            ? '#0d6efd'
            : state.isFocused
                ? '#e9ecef'
                : '#fff',
        color: state.isSelected ? '#fff' : '#212529',
    }),
    menuList: (base) => ({
        ...base,
        maxHeight: PRIMARY_DOMAIN_MENU_MAX_HEIGHT,
        overflowY: 'auto',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const toPrimaryDomainOption = (url) => ({
    value: String(url.id),
    label: url.name || String(url.id),
});

export default class AddDomain extends Component {
    constructor(props) {
        super(props);
        this.primaryDomainSearchTimer = null;
        this.state = {
            domainType: DOMAIN_TYPE_PRIMARY,
            primaryDomain: '',
            selectedPrimaryDomainId: '',
            selectedPrimaryOption: null,
            subDomain: '',
        };
    }

    componentWillUnmount() {
        if (this.primaryDomainSearchTimer) {
            clearTimeout(this.primaryDomainSearchTimer);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.showModal && !this.props.showModal) {
            if (this.primaryDomainSearchTimer) {
                clearTimeout(this.primaryDomainSearchTimer);
            }
            this.setState({
                domainType: DOMAIN_TYPE_PRIMARY,
                primaryDomain: '',
                selectedPrimaryDomainId: '',
                selectedPrimaryOption: null,
                subDomain: '',
            });
        }
    }

    buildPrimaryDomainOptions = () => {
        const landingUrl = Array.isArray(this.props.landingUrl)
            ? this.props.landingUrl
            : [];
        const options = landingUrl.map(toPrimaryDomainOption);
        const { selectedPrimaryOption } = this.state;

        if (
            selectedPrimaryOption &&
            !options.some((option) => option.value === selectedPrimaryOption.value)
        ) {
            return [selectedPrimaryOption, ...options];
        }

        return options;
    };

    resetPrimaryDomainOptions = () => {
        if (this.primaryDomainSearchTimer) {
            clearTimeout(this.primaryDomainSearchTimer);
            this.primaryDomainSearchTimer = null;
        }

        const { onSearchPrimaryDomains } = this.props;
        if (typeof onSearchPrimaryDomains === 'function') {
            onSearchPrimaryDomains('');
        }
    };

    handlePrimaryDomainMenuOpen = () => {
        this.resetPrimaryDomainOptions();
    };

    handlePrimaryDomainMenuClose = () => {
        if (this.primaryDomainSearchTimer) {
            clearTimeout(this.primaryDomainSearchTimer);
            this.primaryDomainSearchTimer = null;
        }
    };

    handlePrimaryDomainInputChange = (inputValue, { action }) => {
        if (action !== 'input-change') {
            return;
        }

        const { onSearchPrimaryDomains } = this.props;
        if (typeof onSearchPrimaryDomains !== 'function') {
            return;
        }

        if (this.primaryDomainSearchTimer) {
            clearTimeout(this.primaryDomainSearchTimer);
        }

        const query = (inputValue || '').trim();
        if (!query) {
            onSearchPrimaryDomains('');
            return;
        }

        this.primaryDomainSearchTimer = setTimeout(() => {
            onSearchPrimaryDomains(query);
        }, PRIMARY_DOMAIN_SEARCH_DEBOUNCE_MS);
    };

    handlePrimaryDomainChange = (option) => {
        this.setState({
            selectedPrimaryDomainId: option ? option.value : '',
            selectedPrimaryOption: option || null,
        });
        this.resetPrimaryDomainOptions();
    };

    handleCreate = () => {
        const { domainType, primaryDomain, selectedPrimaryDomainId, subDomain } = this.state;
        if (domainType === DOMAIN_TYPE_PRIMARY) {
            if (this.props.onPrimarySubmit) this.props.onPrimarySubmit(primaryDomain.trim());
            else if (this.props.handleCreate) this.props.handleCreate();
        } else {
            if (this.props.onSubSubmit) {
                this.props.onSubSubmit({ landingUrlId: selectedPrimaryDomainId, subDomain: subDomain.trim() });
            } else if (this.props.handleSubmit) this.props.handleSubmit();
        }
    };

    render() {
        const {
            showModal,
            hideModal,
            primaryDomainsLoading = false,
            primaryDomainsSearching = false,
            primaryDomainSelectTotal = 0,
        } = this.props;
        const { domainType, primaryDomain, selectedPrimaryOption, subDomain } = this.state;
        const isPrimary = domainType === DOMAIN_TYPE_PRIMARY;
        const primaryDomainOptions = this.buildPrimaryDomainOptions();
        const selectLoading = primaryDomainsLoading || primaryDomainsSearching;
        const showSearchHint =
            primaryDomainSelectTotal > 20 && !primaryDomainsSearching;

        return (
            <Modal show={showModal} onHide={hideModal} className="commonModal">
                <Modal.Header closeButton>
                    <Modal.Title id="example-custom-modal-styling-title">
                        <h5>Add domain</h5>
                        <p>Add the destination domain for user redirection</p>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Select Domain Type</Form.Label>
                        <div className="add-domain-radios d-flex flex-wrap gap-3">
                            <Form.Check
                                type="radio"
                                id="domain-type-primary"
                                name="domainType"
                                label="Primary Domain"
                                checked={isPrimary}
                                onChange={() => this.setState({ domainType: DOMAIN_TYPE_PRIMARY })}
                            />
                            <Form.Check
                                type="radio"
                                id="domain-type-sub"
                                name="domainType"
                                label="Sub Domain"
                                checked={!isPrimary}
                                onChange={() => this.setState({ domainType: DOMAIN_TYPE_SUB })}
                            />
                        </div>
                    </Form.Group>

                    {isPrimary ? (
                        <Form.Group className="mb-0">
                            <Form.Label>Domain</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter domain"
                                value={primaryDomain}
                                onChange={(e) => this.setState({ primaryDomain: e.target.value })}
                            />
                            <p className="text-muted small mt-2 mb-0" style={{ fontSize: '10px' }}>
                                Only enter domain without <span className="text-primary">https://</span> or <span className="text-primary">http://</span>.
                            </p>
                        </Form.Group>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Select Primary Domain</Form.Label>
                                <Select
                                    inputId="add-domain-primary-select"
                                    classNamePrefix="add-domain-select"
                                    options={primaryDomainOptions}
                                    value={selectedPrimaryOption}
                                    onChange={this.handlePrimaryDomainChange}
                                    onMenuOpen={this.handlePrimaryDomainMenuOpen}
                                    onMenuClose={this.handlePrimaryDomainMenuClose}
                                    onInputChange={this.handlePrimaryDomainInputChange}
                                    blurInputOnSelect
                                    placeholder={
                                        primaryDomainsLoading
                                            ? 'Loading domains...'
                                            : 'Search or select primary domain'
                                    }
                                    isClearable
                                    isSearchable
                                    isLoading={selectLoading}
                                    loadingMessage={() =>
                                        primaryDomainsSearching
                                            ? 'Searching domains...'
                                            : 'Loading domains...'
                                    }
                                    filterOption={() => true}
                                    maxMenuHeight={PRIMARY_DOMAIN_MENU_MAX_HEIGHT}
                                    styles={PRIMARY_DOMAIN_SELECT_STYLES}
                                    menuPortalTarget={
                                        typeof document !== 'undefined'
                                            ? document.body
                                            : null
                                    }
                                    menuPosition="fixed"
                                    noOptionsMessage={({ inputValue }) =>
                                        (inputValue || '').trim()
                                            ? 'No domains match your search'
                                            : 'No domains found'
                                    }
                                />
                            </Form.Group>
                            <Form.Group className="mb-0">
                                <Form.Label>Enter Sub Domain</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter sub domain"
                                    value={subDomain}
                                    onChange={(e) => this.setState({ subDomain: e.target.value })}
                                />
                                <p className="text-muted small mt-2 mb-0" style={{ fontSize: '10px' }}>
                                    Only enter domain without <span className="text-primary">https://</span> or <span className="text-primary">http://</span>.
                                </p>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn btn-success" onClick={this.handleCreate}>Add</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
